import Constants from 'expo-constants';
import { useMemo, version as reactVersion } from 'react';

interface VersionInfo {
	version: string;
	buildNumber?: string;
}

type RecordValue = Record<string, unknown>;

const isRecord = (value: unknown): value is RecordValue => {
	return !!value && typeof value === 'object';
};

const resolvePublicVersion = (): string | undefined => {
	const envVersion = process.env.EXPO_PUBLIC_VERSION;
	if (typeof envVersion === 'string' && envVersion.length > 0) {
		return envVersion;
	}

	const extraVersion = Constants.expoConfig?.extra?.EXPO_PUBLIC_VERSION;
	if (typeof extraVersion === 'string' && extraVersion.length > 0) {
		return extraVersion;
	}

	const manifest2Value = (Constants as unknown as { manifest2?: unknown }).manifest2;
	const manifest2Extra = isRecord(manifest2Value) && isRecord(manifest2Value.extra)
		? manifest2Value.extra.EXPO_PUBLIC_VERSION
		: null;
	if (typeof manifest2Extra === 'string' && manifest2Extra.length > 0) return manifest2Extra;

	const manifestValue = Constants.manifest as unknown;
	const manifestExtra = isRecord(manifestValue) && isRecord(manifestValue.extra)
		? manifestValue.extra.EXPO_PUBLIC_VERSION
		: null;
	if (typeof manifestExtra === 'string' && manifestExtra.length > 0) return manifestExtra;

	return undefined;
};

function getVersionInfo(): VersionInfo {
	const publicVersion = resolvePublicVersion();
	let version = publicVersion || reactVersion || 'unknown';
	let buildNumber: string | undefined;

	const manifest2Value = (Constants as unknown as { manifest2?: unknown }).manifest2;
	const manifest2 = isRecord(manifest2Value) ? manifest2Value : null;
	const manifestValue = Constants.manifest as unknown;
	const manifest = isRecord(manifestValue) ? manifestValue : null;

	const manifestCandidate = manifest2 || manifest;
	if (manifestCandidate) {
		const runtimeVersion = manifestCandidate.runtimeVersion;
		if (!publicVersion && typeof runtimeVersion === 'string' && runtimeVersion.length > 0) {
			version = runtimeVersion;
		}

		const id = manifestCandidate.id;
		if (typeof id === 'string' && id.length > 0) {
			buildNumber = id;
		}
	}

	const expoConfigValue = Constants.expoConfig as unknown;
	const expoConfig = isRecord(expoConfigValue) ? expoConfigValue : null;
	if (expoConfig) {
		const expoVersion = expoConfig.version;
		if (!publicVersion && typeof expoVersion === 'string' && expoVersion.length > 0) {
			version = expoVersion;
		}

		const ios = expoConfig.ios;
		if (isRecord(ios) && typeof ios.buildNumber === 'string' && ios.buildNumber.length > 0) {
			buildNumber = ios.buildNumber;
		} else {
			const android = expoConfig.android;
			if (isRecord(android) && (typeof android.versionCode === 'number' || typeof android.versionCode === 'string')) {
				buildNumber = String(android.versionCode);
			} else if (typeof expoConfig.buildVersion === 'number' || typeof expoConfig.buildVersion === 'string') {
				buildNumber = String(expoConfig.buildVersion);
			}
		}
	}

	return { version, buildNumber };
}

export const useVersion = (): VersionInfo => {
	return useMemo(getVersionInfo, []);
};
